import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Menu, X, User, Settings, LogOut, MessageSquare, Calendar, Crown, Sparkles, Palmtree, Bell, Users } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = ({ transparent = false, minimal = false }) => {
  const { user, logout, hasInnerCircleAccess, hasCoachAccess, hasSDCAccess } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch notifications for logged-in users
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;
      try {
        const response = await axios.get(`${API_URL}/api/notifications`);
        setNotifications(response.data || []);
        setUnreadCount(response.data?.filter(n => !n.read).length || 0);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };

    fetchNotifications();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchNotifications();
    }, 120000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchNotifications(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, [user]);

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.post(`${API_URL}/api/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markNotificationRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  // Countdown timer for retreat (June 26, 2026)
  useEffect(() => {
    const retreatDate = new Date('2026-06-26T00:00:00');
    
    const updateCountdown = () => {
      const now = new Date();
      const diff = retreatDate - now;
      
      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setCountdown({ days, hours, minutes, seconds });
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const userMenuItems = [
    { icon: User, label: 'Dashboard', href: '/dashboard' },
    { icon: MessageSquare, label: 'Messages', href: '/messages' },
    { icon: Calendar, label: 'Sessions', href: '/sessions' },
    { icon: Settings, label: 'Settings', href: '/settings' },
  ];

  // Build nav links for logged-in users (used in user menu and mobile)
  const getNavLinks = () => {
    if (!user) return [];
    
    const links = [{ href: '/dashboard', label: 'Dashboard' }];
    
    if (hasInnerCircleAccess()) {
      links.push(
        { href: '/communities', label: 'Communities' },
        { href: '/videos', label: 'The Vault' }
      );
    }
    
    if (hasCoachAccess()) {
      links.push({ href: '/coaches', label: 'Coaches' });
    }
    
    links.push({ href: '/costa-rica-retreat', label: 'Retreat' });
    
    return links;
  };

  const navLinks = getNavLinks();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Minimal navbar for landing page - just retreat info
  if (minimal && !user) {
    return (
      <nav className="fixed top-0 left-0 right-0 z-50 bg-terracotta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Retreat Announcement with Countdown */}
            <div className="flex items-center gap-3 text-white">
              <Palmtree className="h-4 w-4 hidden sm:block flex-shrink-0" />
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                <span className="font-medium text-sm sm:text-base whitespace-nowrap">
                  Just Be: Soft Power™ Retreat
                </span>
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <div className="flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded">
                    <span className="font-bold">{countdown.days}</span>
                    <span className="text-white/80">d</span>
                  </div>
                  <div className="flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded">
                    <span className="font-bold">{countdown.hours}</span>
                    <span className="text-white/80">h</span>
                  </div>
                  <div className="flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded">
                    <span className="font-bold">{countdown.minutes}</span>
                    <span className="text-white/80">m</span>
                  </div>
                  <span className="hidden sm:inline text-white/70 text-xs">until departure</span>
                </div>
              </div>
            </div>

            {/* Links */}
            <div className="flex items-center flex-shrink-0">
              <Link 
                to="/costa-rica-retreat"
                className="text-white font-semibold text-sm underline hover:no-underline"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 ${transparent ? 'bg-transparent' : 'glass-header'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <span className={`font-heading text-xl font-bold ${transparent ? 'text-white' : 'text-forest'}`}>
              Sister Diamonds
            </span>
          </Link>

          {/* Right Side - User Menu */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                {/* Notification Bell */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="notifications-trigger">
                      <Bell className={`h-5 w-5 ${transparent ? 'text-white' : 'text-gray-600'}`} />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-terracotta text-white text-xs flex items-center justify-center font-medium">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-80" align="end">
                    <div className="px-3 py-2 border-b border-sand">
                      <p className="font-medium text-forest">Notifications</p>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="px-3 py-6 text-center text-gray-500 text-sm">
                          No notifications yet
                        </div>
                      ) : (
                        notifications.slice(0, 10).map((notification) => (
                          <DropdownMenuItem
                            key={notification.id}
                            className={`px-3 py-3 cursor-pointer ${!notification.read ? 'bg-terracotta/5' : ''}`}
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className="flex gap-3 w-full">
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                notification.type === 'new_circle_recommendation' ? 'bg-forest/10' : 'bg-terracotta/10'
                              }`}>
                                {notification.type === 'new_circle_recommendation' ? (
                                  <Users className="h-4 w-4 text-forest" />
                                ) : (
                                  <Bell className="h-4 w-4 text-terracotta" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm ${!notification.read ? 'font-medium' : 'text-gray-600'}`}>
                                  {notification.title}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {notification.message}
                                </p>
                                {notification.created_at && (
                                  <p className="text-xs text-gray-400 mt-1">
                                    {new Date(notification.created_at).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                              {!notification.read && (
                                <div className="h-2 w-2 rounded-full bg-terracotta flex-shrink-0 mt-1" />
                              )}
                            </div>
                          </DropdownMenuItem>
                        ))
                      )}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* User Avatar Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-trigger">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user.profile_image} alt={user.full_name} />
                        <AvatarFallback className="bg-forest text-white">
                          {user.full_name?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user.full_name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                      {user.active_tiers?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {user.active_tiers.map(tier => (
                          <span key={tier} className="text-xs px-2 py-0.5 bg-terracotta/10 text-terracotta rounded-full">
                            {tier.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  {userMenuItems.map((item) => (
                    <DropdownMenuItem key={item.href} asChild>
                      <Link to={item.href} className="flex items-center gap-2 cursor-pointer">
                        <item.icon className="h-4 w-4" />
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                  {hasSDCAccess() && (
                    <DropdownMenuItem asChild>
                      <Link to="/sdc" className="flex items-center gap-2 cursor-pointer">
                        <Crown className="h-4 w-4" />
                        SDC Lounge
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
                        <Sparkles className="h-4 w-4" />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <>
                <a 
                  href="https://calendly.com/crystalanddrnicole/justbesoftpowerretreatinterest" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button 
                    variant={transparent ? 'ghost' : 'outline'} 
                    size="sm"
                    className={transparent ? 'text-white border-white/30 hover:bg-white/10' : 'btn-outline'} 
                    data-testid="book-call-btn"
                  >
                    <Palmtree className="h-4 w-4 mr-2" />
                    Book a Call
                  </Button>
                </a>
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className={transparent ? 'text-white hover:bg-white/10' : 'text-forest hover:bg-sand'} 
                    data-testid="login-btn"
                  >
                    Log In
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className={`h-6 w-6 ${transparent ? 'text-white' : 'text-forest'}`} />
            ) : (
              <Menu className={`h-6 w-6 ${transparent ? 'text-white' : 'text-forest'}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white rounded-lg shadow-lg mt-2 p-4">
            <div className="flex flex-col gap-2">
              {user ? (
                <>
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="px-4 py-2 rounded-md hover:bg-sand transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <div className="border-t border-sand my-2" />
                  {userMenuItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="px-4 py-2 rounded-md hover:bg-sand transition-colors flex items-center gap-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <item.icon className="h-4 w-4" />
                      {item.label}
                    </Link>
                  ))}
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 rounded-md hover:bg-red-50 text-red-600 text-left flex items-center gap-2"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <a 
                    href="https://calendly.com/crystalanddrnicole/justbesoftpowerretreatinterest" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button variant="outline" className="w-full btn-outline">
                      <Palmtree className="h-4 w-4 mr-2" />
                      Book a Call
                    </Button>
                  </a>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full">Log In</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
